import { useState, type ReactNode } from 'react';
import { Box, Paper, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { MarkdownContent } from '../../../shared/components/MarkdownContent';

// One fixed height for the editor and the preview, so switching mode never
// resizes the step; the content scrolls inside.
const EDITOR_HEIGHT = { xs: 320, sm: 420 };

export function isDescriptionValid(value: string, required: boolean): boolean {
  return !required || value.trim().length > 0;
}

interface DescriptionStepProps {
  value: string;
  onChange: (value: string) => void;
  heading: string;
  helper: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  /** Rendered at the left of the Edit / Preview toggle — e.g. a template switch. */
  toolbarStart?: ReactNode;
  /** Focus the editor on mount; off when the step has fields before it. */
  autoFocus?: boolean;
}

/**
 * Free-text description in Markdown with an Edit / Preview toggle. Type-
 * agnostic: the wording, and anything offered in the toolbar such as
 * templates, come from the wizard that renders it.
 */
export function DescriptionStep({
  value,
  onChange,
  heading,
  helper,
  label,
  placeholder,
  required = false,
  toolbarStart,
  autoFocus = true,
}: DescriptionStepProps) {
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" component="h2" fontWeight={700}>
          {heading}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {helper}
        </Typography>
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent={toolbarStart ? 'space-between' : 'flex-end'}
        flexWrap="wrap"
        gap={1.5}
      >
        {toolbarStart}
        <ToggleButtonGroup
          value={mode}
          exclusive
          size="small"
          aria-label="Editor mode"
          onChange={(_, next) => {
            if (next) setMode(next);
          }}
        >
          <ToggleButton value="edit" sx={{ gap: 0.5, px: 1.5 }}>
            <EditNoteIcon fontSize="small" />
            <Typography variant="caption" fontWeight={600}>
              Edit
            </Typography>
          </ToggleButton>
          <ToggleButton value="preview" sx={{ gap: 0.5, px: 1.5 }}>
            <VisibilityOutlinedIcon fontSize="small" />
            <Typography variant="caption" fontWeight={600}>
              Preview
            </Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Box sx={{ height: EDITOR_HEIGHT, display: 'flex', flexDirection: 'column' }}>
        {mode === 'edit' ? (
          <TextField
            label={label}
            required={required}
            multiline
            fullWidth
            autoFocus={autoFocus}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            sx={{
              flex: 1,
              minHeight: 0,
              '& .MuiInputBase-root': {
                height: '100%',
                alignItems: 'flex-start',
                overflow: 'auto',
                bgcolor: 'background.paper',
                fontFamily: 'var(--font-mono)',
                fontSize: '0.875rem',
              },
              // TextareaAutosize sets an inline height; pin it to the box instead.
              '& .MuiInputBase-input': {
                height: '100% !important',
                overflowY: 'auto !important',
                boxSizing: 'border-box',
              },
            }}
          />
        ) : (
          <Paper sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 3 }}>
            {value.trim() ? (
              <MarkdownContent>{value}</MarkdownContent>
            ) : (
              <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                Nothing to preview yet. Switch to Edit and write your description.
              </Typography>
            )}
          </Paper>
        )}
      </Box>
    </Stack>
  );
}
